// ─────────────────────────────────────────────────────────────────────────
// InAppBrowserNotice.tsx
// Dismissible banner shown when the driver opened this job-sheet link
// inside LINE's (or Facebook's/Instagram's) in-app browser, where the live
// camera often doesn't work and silently falls back to a plain file
// picker. Nudges the driver to open the link in their real browser instead.
// ─────────────────────────────────────────────────────────────────────────

import { useState } from 'react';
import { ArrowSquareOut, Browser, X } from '@phosphor-icons/react';
import { useLanguage } from './LanguageContext';
import { useInAppBrowser } from './useInAppBrowser';

export default function InAppBrowserNotice() {
  const { t } = useLanguage();
  const { kind, isInAppBrowser, tryOpenExternal } = useInAppBrowser();
  const [dismissed, setDismissed] = useState(false);

  if (!isInAppBrowser || dismissed) return null;

  return (
    <div className="border-b-2 border-amber-300 bg-amber-50 px-4 py-3 text-amber-900">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-amber-200">
          <Browser weight="fill" className="text-base text-amber-800" />
        </div>

        <div className="flex-1">
          <p className="text-sm font-bold">{t('inAppBrowserTitle')}</p>
          <p className="mt-0.5 text-xs leading-relaxed">
            {kind === 'line' ? t('inAppBrowserBodyLine') : t('inAppBrowserBodyGeneric')}
          </p>

          {kind === 'line' ? (
            // LINE supports an automatic hand-off to the real browser.
            <button
              type="button"
              onClick={() => tryOpenExternal()}
              className="mt-2 flex items-center gap-1.5 rounded-lg bg-amber-900 px-3 py-2 text-xs font-bold text-white shadow-sm transition active:scale-95"
            >
              <ArrowSquareOut weight="bold" />
              {t('openInBrowserButton')}
            </button>
          ) : (
            // Facebook/Instagram have no equivalent trick — manual steps only.
            <p className="mt-2 text-xs font-semibold">{t('inAppBrowserManualGeneric')}</p>
          )}

          {kind === 'line' && (
            <p className="mt-1.5 text-[11px] text-amber-700">{t('inAppBrowserManualLine')}</p>
          )}
        </div>

        <button
          type="button"
          onClick={() => setDismissed(true)}
          aria-label="Dismiss"
          className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-amber-700 transition hover:bg-amber-200 active:bg-amber-300"
        >
          <X weight="bold" />
        </button>
      </div>
    </div>
  );
}
